/**
 * HopnModal — main modal step router (sessionStorage-driven).
 */

import React, {useEffect, useState, useRef} from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {MODAL_STEPS, POPUP_STEPS, normalizeStep} from '@/constants/steps';
import {storage} from '@/utils/storage';
import {STORAGE_KEYS} from '@/constants/storageKeys';
import {BookingModalShell} from './BookingModalShell.jsx';
import {primeIOSKeyboard} from '@/utils/iosFocusProxy';
import {isIOS} from '@/utils/platform';
import {MODAL_CLOSE_MS, modalContainerClass, modalOverlayClass} from '@/utils/modalAnim.js';
import {useAppConfig} from '@/hooks/useAppConfig';
import {usePolicies} from '@/hooks/usePolicies';
import {useAirports} from '@/hooks/useAirports';

import {PassengerQuantity} from '../modalPages/passengerQuantity.jsx';
import {ChildSeatsDetails} from '../modalPages/childSeatsDetails.jsx';
import {PassengerLuggage} from '../modalPages/passengerLuggage.jsx';
import {OverSizeLuggage} from '../modalPages/overSizeLuggage.jsx';
import {TravelingDateTime} from '../modalPages/travelingDateTime.jsx';
import {TravelingDate} from '../modalPages/travelingDate.jsx';
import {CostCalculation} from '../modalPages/costCalculation.jsx';
import {ApplyCoupon} from '../modalPages/applyCoupon.jsx';
import {PricingBreakdown} from '../modalPages/pricingBreakdown.jsx';
import {SpecialRequest} from '../modalPages/specialRequest.jsx';
import {PayAfterDropoff} from '../modalPages/payAfterDropoff.jsx';
import {InterestFreePayment} from '../modalPages/interestFreePayment.jsx';
import {AfterPay} from '../modalPages/afterPay.jsx';
import {NoSurprises} from '../modalPages/noSurprises.jsx';
import {DownloadAppReminder} from '../modalPages/downloadAppReminder.jsx';
import {AirportPickupFeeInfoStep} from '../modalPages/airportPickupFeeInfoStep.jsx';
import {FlightDelayed} from '../modalPages/flightDelayed.jsx';
import {WaitingTimeInfo} from '../modalPages/waitingTimeInfo.jsx';
import {MainPolicies} from '../modalPages/mainPolicies.jsx';
import {CompleteProfile} from '../modalPages/completeProfile.jsx';
import {CancellationPolicies} from '../modalPages/cancellationPolicies.jsx';
import {FindingDriverPolicies} from '../modalPages/findingDriverPolicies.jsx';
import {TollPolicies} from '../modalPages/tollPolicies.jsx';
import {PassengerPolicies} from '../modalPages/passengerPolicies.jsx';
import {RefundPolicies} from '../modalPages/refundPolicies.jsx';
import {DriverPolicies} from '../modalPages/driverPolicies.jsx';
import {PickupPolicies} from '../modalPages/pickupPolicies.jsx';
import {PoliciesListing} from '../modalPages/policiesListing.jsx';
import {PoliciesExtraDetails} from '../modalPages/policiesExtraDetails.jsx';
import {TermOfServices} from '../modalPages/termOfServices.jsx';
import {PrivacyPolicy} from '../modalPages/privacyPolicy.jsx';
import {BookingReceived} from '../modalPages/bookingReceived.jsx';
import {FlightAirportTo} from '../modalPages/flightAirportTo.jsx';
import {AirportPickupPerks} from '../modalPages/airportPickupPerks.jsx';
import {FindingDriver} from '../modalPages/findingDriver.jsx';
// CarSelection is disabled — the flow now goes straight from the date/time step to
// SelectedCarDetailsLatest. Kept imported for an easy revert (restore the
// CAR_SELECTION mapping below and point the callers back at 'carSelection').
import {CarSelection} from '../modalPages/carSelection.jsx';
import {OrderDetails} from '../modalPages/orderDetails.jsx';
import {PaymentCards, PersonalCard} from '../modalPages/paymentCards.jsx';
import {ProcessingBooking} from '../modalPages/processingBooking.jsx';
import {TravelingTime} from '../modalPages/travelingTime.jsx';
import {SmartTimeSelect} from '../modalPages/smartTimeSelect.jsx';
import {SmartPickupRecommendation} from '../modalPages/smartPickupRecommendation.jsx';
import {SmartPickupResult} from '../modalPages/smartPickupResult.jsx';
import {FlightAirportDetails} from '../modalPages/flightAirportDetails.jsx';
import {FlightAirportLists} from '../modalPages/flightAirportLists.jsx';
import {FlightAirlines} from '../modalPages/flightAirlines.jsx';
import {FlightNumber} from '../modalPages/flightNumber.jsx';
import {RecommendedPickupDetails} from '../modalPages/recommendedPickupDetails.jsx';
import {LiabilityDisclaimer} from '../modalPages/liabilityDisclaimer.jsx';
import {SelectedCarDetailsLatest} from '../modalPages/selectedCarDetailsLatest.jsx';
import {PopularQuestions} from '../modalPages/popularQuestions.jsx';
import {PrebookCompare} from '../modalPages/prebookCompare.jsx';
import {CancellationModificationRefund} from '../modalPages/cancellationModificationRefund.jsx';
import {WhyChooseUsStep} from '../modalPages/whyChooseUs.jsx';

const STEP_COMPONENTS = {
    [MODAL_STEPS.PASSENGER_QUANTITY]: PassengerQuantity,
    [MODAL_STEPS.CHILD_SEATS_DETAILS]: ChildSeatsDetails,
    [MODAL_STEPS.PASSENGER_LUGGAGE]: PassengerLuggage,
    [MODAL_STEPS.OVER_SIZE_LUGGAGE]: OverSizeLuggage,
    [MODAL_STEPS.TRAVELING_DATE_TIME]: TravelingDateTime,
    [MODAL_STEPS.TRAVELING_DATE]: TravelingDate,
    [MODAL_STEPS.COST_CALCULATION]: CostCalculation,
    [MODAL_STEPS.APPLY_COUPON]: ApplyCoupon,
    [MODAL_STEPS.PAYMENT_BREAKDOWN]: PricingBreakdown,
    [MODAL_STEPS.SPECIAL_REQUEST]: SpecialRequest,
    [MODAL_STEPS.PAY_AFTER_DROPOFF]: PayAfterDropoff,
    [MODAL_STEPS.INTEREST_FREE_PAYMENT]: InterestFreePayment,
    [MODAL_STEPS.AFTER_PAY]: AfterPay,
    [MODAL_STEPS.NO_SURPRISES]: NoSurprises,
    [MODAL_STEPS.DOWNLOAD_APP_REMINDER]: DownloadAppReminder,
    [MODAL_STEPS.AIRPORT_PICKUP_FEE_INFO]: AirportPickupFeeInfoStep,
    [MODAL_STEPS.FLIGHT_DELAYED]: FlightDelayed,
    [MODAL_STEPS.WAITING_TIME_INFO]: WaitingTimeInfo,
    [MODAL_STEPS.MAIN_POLICIES]: MainPolicies,
    [MODAL_STEPS.COMPLETE_PROFILE]: CompleteProfile,
    [MODAL_STEPS.CANCELLATION_POLICIES]: CancellationPolicies,
    [MODAL_STEPS.FINDING_DRIVER_POLICIES]: FindingDriverPolicies,
    [MODAL_STEPS.TOLL_POLICIES]: TollPolicies,
    [MODAL_STEPS.PASSENGER_POLICIES]: PassengerPolicies,
    [MODAL_STEPS.REFUND_POLICIES]: RefundPolicies,
    [MODAL_STEPS.DRIVER_POLICIES]: DriverPolicies,
    [MODAL_STEPS.PICKUP_POLICIES]: PickupPolicies,
    [MODAL_STEPS.POLICIES_LISTING]: PoliciesListing,
    [MODAL_STEPS.POLICIES_EXTRA_DETAILS]: PoliciesExtraDetails,
    [MODAL_STEPS.TERM_OF_SERVICES]: TermOfServices,
    [MODAL_STEPS.PRIVACY_POLICY]: PrivacyPolicy,
    [MODAL_STEPS.BOOKING_RECEIVED]: BookingReceived,
    [MODAL_STEPS.FLIGHT_AIRPORT_TO]: FlightAirportTo,
    [MODAL_STEPS.AIRPORT_PICKUP_PERKS]: AirportPickupPerks,
    [MODAL_STEPS.FINDING_DRIVER]: FindingDriver,
    [MODAL_STEPS.TRAVELING_TIME]: TravelingTime,
    [MODAL_STEPS.SMART_TIME_SELECT]: SmartTimeSelect,
    [MODAL_STEPS.SMART_PICKUP_RECOMMENDATION]: SmartPickupRecommendation,
    [MODAL_STEPS.SMART_PICKUP_RESULT]: SmartPickupResult,
    [MODAL_STEPS.FLIGHT_AIRPORT_DETAIL]: FlightAirportDetails,
    [MODAL_STEPS.FLIGHT_AIRPORT_LISTS]: FlightAirportLists,
    [MODAL_STEPS.FLIGHT_AIRLINES]: FlightAirlines,
    // [MODAL_STEPS.CAR_SELECTION]: CarSelection,  // disabled — see below
    // carSelection disabled — aliased to SelectedCarDetailsLatest so a stray navigation
    // (or a persisted latest-step from before this change) still lands on a real screen.
    [MODAL_STEPS.CAR_SELECTION]: SelectedCarDetailsLatest,
    [MODAL_STEPS.SELECTED_CAR_DETAILS]: SelectedCarDetailsLatest,
    [MODAL_STEPS.POPULAR_QUESTIONS]: PopularQuestions,
    [MODAL_STEPS.PREBOOK_COMPARE]: PrebookCompare,
    [MODAL_STEPS.CANCELLATION_MODIFICATION_REFUND]: CancellationModificationRefund,
    [MODAL_STEPS.WHY_CHOOSE_US]: WhyChooseUsStep,
    [MODAL_STEPS.ORDER_DETAILS]: OrderDetails,
    [MODAL_STEPS.PAYMENT_CARDS]: PaymentCards,
    [MODAL_STEPS.PERSONAL_CARD]: PersonalCard,
    [MODAL_STEPS.PROCESSING_BOOKING]: ProcessingBooking,
    [MODAL_STEPS.RECOMMENDED_PICKUP_DETAILS]: RecommendedPickupDetails,
    [MODAL_STEPS.LIABILITY_DISCLAIMER]: LiabilityDisclaimer,
    flightNumber: FlightNumber,
};

export function HopnModal({step, setStep, closeModal, airportCase = false, previousStep = null}) {
    // Boot-gate: every booking step page depends on /config/app (it powers
    // BOOKING_CONSTRAINTS — minimum lead time, gratuity options, max advance days…).
    // Subscribe so the modal re-renders once the config lands and block the step
    // pages until it's cached, so they never read the hardcoded default fallbacks.
    const {data: appConfig, isError, refetch, isFetching} = useAppConfig();

    // Same gate for /trips/calculate-policies — fetched & cached on modal mount
    // (powers POLICY_CONSTANTS and the policy popups). Block the step pages until
    // both config and policies are cached; readable via getQueryData(POLICIES_KEY).
    const {
        data: policies,
        isError: policiesIsError,
        refetch: policiesRefetch,
        isFetching: policiesIsFetching,
    } = usePolicies();

    // Same gate for /config/airports. It backs the airport DETECTION
    // (utils/airports.js), which is synchronous and reports "not an airport" against an
    // empty cache — a step page that rendered before this landed would show a
    // non-airport flow for an airport trip. Fetched at app root too, so this is
    // normally instant.
    const {
        data: airports,
        isError: airportsIsError,
        refetch: airportsRefetch,
        isFetching: airportsIsFetching,
    } = useAirports();

    // Mobile-only: nested popup steps open as stacked modal overlays on top of the
    // current screen instead of replacing it. `modalStack` is the overlay's own step
    // stack ([] = closed); the underlying main step stays mounted. See POPUP_STEPS.
    // EACH level renders as its OWN <Modal> (see the render below), so a nested step
    // (e.g. a policy opened from the policies list) slides up as a fresh sheet OVER its
    // still-mounted parent — the SAME entrance animation as the base modal — rather than
    // swapping content in place. The parent stays visible/dimmed behind the child.
    // Declared with the hooks (never after an early return) to avoid React error #310.
    const [modalStack, setModalStack] = useState([]);
    // `renderStack` mirrors `modalStack` but RETAINS a just-popped level through its
    // slide-down so the exit animation can play; each Modal's onAfterClose prunes the
    // trailing closed levels. `modalStackRef` keeps the latest open stack readable in
    // that async callback.
    const [renderStack, setRenderStack] = useState([]);
    const modalStackRef = useRef(modalStack);
    modalStackRef.current = modalStack;

    useEffect(() => {
        const container = document.querySelector('.hopn-modal-container-inner');
        if (!container) return;

        if (window.innerWidth <= 575 && step === MODAL_STEPS.ORDER_DETAILS) {
            const savedScroll = storage.get(STORAGE_KEYS.ORDER_DETAILS_SCROLL, null);
            storage.remove(STORAGE_KEYS.ORDER_DETAILS_SCROLL);
            if (savedScroll) {
                setTimeout(() => {
                    container.scrollTop = parseInt(savedScroll, 10);
                }, 100);
                return;
            }
        }

        setTimeout(() => {
            container.scrollTop = 0;
        }, 50);
    }, [step]);

    // Keep `renderStack` aligned with the open `modalStack`: newly pushed levels appear
    // immediately (they mount → slide up), while popped levels are LEFT in place so their
    // slide-down can play — each Modal's onAfterClose trims the trailing closed levels.
    useEffect(() => {
        setRenderStack((prev) => {
            const next = prev.slice();
            modalStack.forEach((s, i) => {
                if (next[i] !== s) next[i] = s;
            });
            return next;
        });
    }, [modalStack]);

    // modalPages gate — don't render any step page until BOTH /config/app and the
    // calculated policies are cached, so steps never read default fallbacks.
    if (!appConfig || !policies || !airports) {
        const configFailed = isError && !isFetching;
        const policiesFailed = policiesIsError && !policiesIsFetching;
        const airportsFailed = airportsIsError && !airportsIsFetching;
        if (configFailed || policiesFailed || airportsFailed) {
            return (
                <div className="p-10 text-center text-(--text-secondary)">
                    <p>Online Booking is unavailable now</p>
                    <button
                        onClick={() => {
                            if (configFailed) refetch();
                            if (policiesFailed) policiesRefetch();
                            if (airportsFailed) airportsRefetch();
                        }}
                        className="hopn-add-passenger-button no-icon grey mt-4"
                    >
                        Retry
                    </button>
                </div>
            );
        }
        return (
            <div className="flex justify-center p-15">
                <Loader color="#201e20"/>
            </div>
        );
    }

    const normalizedStep = normalizeStep(step);
    const StepComponent = STEP_COMPONENTS[normalizedStep];

    if (!StepComponent) {
        return (
            <div style={{padding: '40px', textAlign: 'center', color: '#666'}}>
                <p>Step not found: {step}</p>
                <button onClick={() => setStep(MODAL_STEPS.PASSENGER_QUANTITY)}>Go to Start</button>
            </div>
        );
    }

    // Overlay controls (mobile nested-popup steps).
    const openModalStep = (s) => setModalStack([s]);
    // Header ✕ / Esc: pop one level — closes the overlay at the last level, or reveals
    // the previous view for a multi-level stack (e.g. policies list → a policy).
    const popModalStep = () => setModalStack((stack) => stack.slice(0, -1));

    // Main step's setStep: on mobile, popup-type targets open as an overlay (keeping
    // the current screen) rather than swapping the router.
    const smartSetStep = (target) => {
        // The coupon step auto-focuses its field after the sheet slides in; prime the iOS
        // keyboard now, inside the tap that opened it (see utils/iosFocusProxy).
        if (normalizeStep(target) === MODAL_STEPS.APPLY_COUPON) primeIOSKeyboard();
        if (typeof window !== 'undefined' && window.innerWidth < 576 && POPUP_STEPS.has(normalizeStep(target))) {
            openModalStep(target);
        } else {
            setStep(target);
        }
    };

    // setStep handed to a step rendered INSIDE the overlay: back → pop, deeper popup
    // → push, real flow step → close the overlay and navigate the main router.
    const overlaySetStep = (target) => {
        const below = modalStack.length >= 2 ? modalStack[modalStack.length - 2] : normalizedStep;
        if (normalizeStep(target) === normalizeStep(below)) {
            setModalStack(modalStack.slice(0, -1));
        } else if (POPUP_STEPS.has(normalizeStep(target))) {
            setModalStack([...modalStack, target]);
        } else {
            setModalStack([]);
            setStep(target);
        }
    };

    // The stacked overlay (mobile) — ONE <Modal> per level in `renderStack`. A pushed
    // level mounts as a fresh sheet and slides up (the SAME entrance animation as the
    // base modal) OVER its still-mounted parent (parent stays visible/dimmed, not
    // closed); ✕/back pops the top, which slides down and is then pruned by onAfterClose.
    // Each level renders in its own BookingModalShell so its ModalHeader/ModalFooter
    // portal into that overlay. `previousStep` = the level below → the step's back target.
    return (
        <>
            <StepComponent
                step={normalizedStep}
                setStep={smartSetStep}
                previousStep={previousStep}
                closeModal={closeModal}
                airportCase={airportCase}
                openModalStep={openModalStep}
            />
            {renderStack.map((rawStep, i) => {
                const overlayStep = normalizeStep(rawStep);
                const OverlayComponent = STEP_COMPONENTS[overlayStep];
                // Open while this level is still in the live stack; false once popped —
                // which plays the slide-down, then onAfterClose trims the closed levels.
                const isOpen = i < modalStack.length && modalStack[i] === rawStep;
                const below = i >= 1 ? normalizeStep(renderStack[i - 1]) : normalizedStep;
                return (
                    <Modal
                        key={`${i}-${overlayStep}`}
                        isOpen={isOpen}
                        onRequestClose={popModalStep}
                        onAfterClose={() => setRenderStack((st) => st.slice(0, modalStackRef.current.length))}
                        closeTimeoutMS={MODAL_CLOSE_MS}
                        overlayClassName={modalOverlayClass('hopn-modal-overlay--stacked')}
                        className={modalContainerClass(`step-${overlayStep}`)}
                        shouldCloseOnOverlayClick={false}
                        // iOS: react-modal focuses its container on open, which pulls focus off the
                        // keyboard proxy (utils/iosFocusProxy) and closes the keyboard before the step's
                        // field can take it. Off iOS keep the default (a11y focus into the dialog).
                        shouldFocusAfterRender={!isIOS()}
                        ariaHideApp={false}
                        style={{overlay: {zIndex: 10001 + i}}}
                    >
                        {OverlayComponent && (
                            <BookingModalShell step={overlayStep} overlay={true}>
                                <OverlayComponent
                                    step={overlayStep}
                                    setStep={overlaySetStep}
                                    closeModal={popModalStep}
                                    airportCase={airportCase}
                                    previousStep={below}
                                    openModalStep={openModalStep}
                                />
                            </BookingModalShell>
                        )}
                    </Modal>
                );
            })}
        </>
    );
}

HopnModal.propTypes = {
    step: PropTypes.string.isRequired,
    setStep: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    airportCase: PropTypes.bool,
};

export default HopnModal;
